package com.trevorwiebe.timesheet.core.presentation.common

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.DatePicker
import androidx.compose.material3.DatePickerDefaults
import androidx.compose.material3.DatePickerDialog
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TimePicker
import androidx.compose.material3.TimePickerDefaults
import androidx.compose.material3.rememberDatePickerState
import androidx.compose.material3.rememberTimePickerState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import com.trevorwiebe.timesheet.core.domain.Util
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime

private enum class DateTimeStep { DATE, TIME }


@OptIn(ExperimentalMaterial3Api::class)
@Composable
actual fun NativeDateTimePicker(
    dateTime: LocalDateTime,
    showPicker: Boolean,
    onDismiss: () -> Unit,
    onDateTimeSelected: (LocalDateTime) -> Unit,
) {
    if (!showPicker) return

    var step by remember { mutableStateOf(DateTimeStep.DATE) }
    var pendingDate by remember { mutableStateOf<LocalDate?>(null) }

    LaunchedEffect(showPicker) {
        if (!showPicker) {
            step = DateTimeStep.DATE
            pendingDate = null
        }
    }

    val datePickerState = rememberDatePickerState(
        initialSelectedDateMillis = dateTime.date.toEpochDays() * 86_400_000L
    )
    val timePickerState = rememberTimePickerState(
        initialHour = dateTime.hour,
        initialMinute = dateTime.minute,
        is24Hour = false
    )

    when (step) {
        DateTimeStep.DATE -> {
            DatePickerDialog(
                onDismissRequest = onDismiss,
                confirmButton = {
                    TextButton(
                        onClick = {
                            datePickerState.selectedDateMillis?.let { millis ->
                                val selected = Util.datePickerMillisToDate(millis)
                                pendingDate = selected
                                step = DateTimeStep.TIME
                            }
                        },
                        colors = ButtonDefaults.buttonColors(
                            contentColor = MaterialTheme.colorScheme.onTertiary,
                            containerColor = MaterialTheme.colorScheme.tertiary
                        )
                    ) { Text("Next") }
                },
                dismissButton = {
                    TextButton(
                        onClick = onDismiss,
                        colors = ButtonDefaults.buttonColors(
                            contentColor = MaterialTheme.colorScheme.tertiary,
                            containerColor = MaterialTheme.colorScheme.background
                        )
                    ) { Text("Cancel") }
                }
            ) {
                DatePicker(
                    state = datePickerState,
                    dateFormatter = webDatePickerFormatter,
                    headline = {
                        val headlineDate = datePickerState.selectedDateMillis?.let(Util::datePickerMillisToDate)
                        Text(
                            text = Util.toShortMonthDayYear(headlineDate),
                            style = MaterialTheme.typography.headlineLarge,
                            modifier = Modifier.padding(start = 24.dp, end = 12.dp, bottom = 12.dp)
                        )
                    },
                    colors = DatePickerDefaults.colors(
                        selectedDayContainerColor = MaterialTheme.colorScheme.tertiary,
                        selectedDayContentColor = MaterialTheme.colorScheme.onTertiary,
                        todayDateBorderColor = MaterialTheme.colorScheme.tertiary,
                        todayContentColor = MaterialTheme.colorScheme.onBackground,
                    )
                )
            }
        }

        DateTimeStep.TIME -> {
            Dialog(onDismissRequest = onDismiss) {
                Card(shape = RoundedCornerShape(16.dp)) {
                    Column(modifier = Modifier.padding(16.dp)) {
                        TimePicker(
                            state = timePickerState,
                            colors = TimePickerDefaults.colors(
                                clockDialColor = MaterialTheme.colorScheme.surface,
                                clockDialSelectedContentColor = MaterialTheme.colorScheme.onTertiary,
                                selectorColor = MaterialTheme.colorScheme.tertiary,
                                containerColor = MaterialTheme.colorScheme.onTertiary,
                                periodSelectorSelectedContainerColor = MaterialTheme.colorScheme.tertiary,
                                periodSelectorSelectedContentColor = MaterialTheme.colorScheme.onTertiary,
                                periodSelectorUnselectedContentColor = MaterialTheme.colorScheme.onSurface,
                                timeSelectorSelectedContentColor = MaterialTheme.colorScheme.onTertiary,
                                timeSelectorSelectedContainerColor = MaterialTheme.colorScheme.tertiary,
                            )
                        )
                        DialogButtonRow(
                            confirmButtonText = "OK",
                            onConfirmClick = {
                                val date = pendingDate ?: dateTime.date
                                val result = LocalDateTime(
                                    year = date.year,
                                    monthNumber = date.monthNumber,
                                    dayOfMonth = date.dayOfMonth,
                                    hour = timePickerState.hour,
                                    minute = timePickerState.minute,
                                    second = 0,
                                    nanosecond = 0
                                )
                                onDateTimeSelected(result)
                            },
                            dismissButtonText = "Back",
                            onDismissClick = { step = DateTimeStep.DATE },
                        )
                    }
                }
            }
        }
    }
}
